import React from 'react'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../generic/systemValues/systemValueLabel'
import { dayMonthOf, formatOneDecimal, formatSignedDays, weekNumberOfPeriodKey } from './loadCapacityFormat'
import MachineDetailSection from './MachineDetailSection'

// Contract §1 -- the four delivery states and how each one is coloured (RN22 marks). Story 204 (RN1.5): the label is
// the status code itself, translated through systemValueLabel ('DeliveryStatus').
const DELIVERY_STATUS_PILL = {
  LATE: { className: 'mdd__pill mdd__pill--late' },
  ON_TIME: { className: 'mdd__pill mdd__pill--on-time' },
  EARLY: { className: 'mdd__pill mdd__pill--early' },
  NO_TARGET_DATE: { className: 'mdd__pill' }
}

const NO_VALUE = '–'

// One of the three facts of a row: a small uppercase label over its value, the ISO week as a quiet tag.
const FactCell = ({ label, value, week = null, testId }) => (
  <span className='mdd__fo-fact'>
    <span className='mdd__fo-fact-label'>{label}</span>
    <span className='mdd__fo-fact-value' data-testid={testId}>
      {value}
      {week ? <span className='mdd__fo-week'>{week}</span> : null}
    </span>
  </span>
)

const FabricationOrderEntry = ({ fabricationOrder, selected, onClick }) => {
  const { t_, i18n } = useSmartTranslation()
  const locale = i18n ? i18n.language : undefined
  const weekOf = (periodKey) => {
    const week = weekNumberOfPeriodKey(periodKey)
    return week === null ? null : t_('W{{week}}', { week })
  }
  const hasRequiredDate = fabricationOrder.requiredDate !== null && fabricationOrder.requiredDate !== undefined
  const hasDeviation = fabricationOrder.deviationDays !== null && fabricationOrder.deviationDays !== undefined
  const isLate = fabricationOrder.deliveryStatus === 'LATE'
  const pill = DELIVERY_STATUS_PILL[fabricationOrder.deliveryStatus]
  // Code and name of the part reference, the name only when it adds something (they are often the same string).
  const referenceCode = fabricationOrder.partReferenceCode || ''
  const referenceName = fabricationOrder.partReferenceName || ''
  const reference = referenceName !== '' && referenceName !== referenceCode
    ? [referenceCode, referenceName].filter((part) => part !== '').join(' · ')
    : referenceCode
  const deviationTone = { LATE: 'mdd__fo-deviation--late', EARLY: 'mdd__fo-deviation--early' }[fabricationOrder.deliveryStatus] || ''
  const className = ['mdd__fo', selected ? 'mdd__fo--selected' : ''].filter(Boolean).join(' ')

  // An inset-grouped list row (PO request, 2026-09-29): who the order is on the left, how its delivery is going
  // on the right, and the dates and the load on this machine as one quiet line underneath. No red rail for a
  // late order: the status pill and the deviation already say it.
  return (
    <button
      type='button'
      className={className}
      onClick={() => onClick(fabricationOrder.fabricationOrderId)}
      aria-pressed={selected ? 'true' : 'false'}
      data-testid={'machine-fo-' + fabricationOrder.fabricationOrderId}
      data-late={isLate ? 'true' : 'false'}
    >
      <span className='mdd__fo-head'>
        <span className='mdd__fo-who'>
          <span className='mdd__fo-code'>
            {fabricationOrder.fabricationOrderCode}
            {fabricationOrder.forecast ? <span className='mdd__pill mdd__pill--forecast'>{t_('Forecast')}</span> : null}
          </span>
          {reference !== '' ? <span className='mdd__fo-ref'>{reference}</span> : null}
        </span>
        <span className='mdd__fo-status'>
          <strong className={['mdd__fo-deviation', deviationTone].filter(Boolean).join(' ')} data-testid='machine-fo-deviation'>
            {hasDeviation ? t_('{{days}} d', { days: formatSignedDays(fabricationOrder.deviationDays) }) : NO_VALUE}
          </strong>
          {pill
            ? <span className={pill.className} data-testid='machine-fo-status' data-status={fabricationOrder.deliveryStatus}>{systemValueLabel(t_, 'DeliveryStatus', fabricationOrder.deliveryStatus)}</span>
            : null}
        </span>
      </span>
      <span className='mdd__fo-facts'>
        <FactCell
          label={t_('Required date')}
          testId='machine-fo-required'
          value={hasRequiredDate ? dayMonthOf(fabricationOrder.requiredDate) : NO_VALUE}
          week={hasRequiredDate ? weekOf(fabricationOrder.requiredPeriodKey) : null}
        />
        <FactCell
          label={t_('Expected end')}
          testId='machine-fo-end'
          value={dayMonthOf(fabricationOrder.expectedEndDate)}
          week={weekOf(fabricationOrder.expectedEndPeriodKey)}
        />
        <FactCell
          label={t_('Load (h)')}
          testId='machine-fo-load'
          value={`${formatOneDecimal(fabricationOrder.loadHoursOnDevice, locale)} h`}
        />
      </span>
    </button>
  )
}

/**
 * Story 175, CU6 (S94-S96) -- the fabrication orders with at least one job on this machine, in the order
 * Sun sent them (RN22/RN58: deviation descending, no date last). The count and the late count are Sun's
 * `fabricationOrderCount`/`lateFabricationOrderCount`, never the length of the list (INV-I). Pressing an
 * entry toggles the ONE selected fabrication order of the Planner (RN45); the drawer stays open.
 */
const MachineFabricationOrdersList = ({ detail, selectedFabricationOrderId, onFabricationOrderClick }) => {
  const { t_ } = useSmartTranslation()
  return (
    <MachineDetailSection
      testId='machine-fo-list'
      title={t_('FOs on this machine')}
      caption={t_('{{count}} FOs · {{late}} late', { count: detail.fabricationOrderCount, late: detail.lateFabricationOrderCount })}
      captionTestId='machine-fo-caption'
    >
      {detail.fabricationOrders.length > 0
        ? (
          <div className='mdd__fo-list'>
            {detail.fabricationOrders.map((fabricationOrder) => (
              <FabricationOrderEntry
                key={fabricationOrder.fabricationOrderId}
                fabricationOrder={fabricationOrder}
                selected={fabricationOrder.fabricationOrderId === selectedFabricationOrderId}
                onClick={onFabricationOrderClick}
              />
            ))}
          </div>
          )
        : null}
    </MachineDetailSection>
  )
}

export default MachineFabricationOrdersList
